import { useState } from 'react';
import { ArrowRight, Gamepad2, Play, Radio, Smartphone, Sparkles, Truck, Wrench } from 'lucide-react';
import Seo from '../components/Seo';
import DispatchGamePanel from '../components/DispatchGamePanel';
import GameAccountPanel from '../components/GameAccountPanel';
import { seasonalStateForDate } from '../games/dispatch-dash/seasonal';
import './Games.css';

const dispatchDashKeyArt = '/games/dispatch-dash/j1-wrecker-key-art.webp';
const clearTheLaneKeyArt = '/games/dispatch-dash/clear-the-lane-j1-key-art.webp';
const hookAndHaulKeyArt = '/games/dispatch-dash/hook-and-haul-j20-key-art.webp';
const dispatchDashHalloweenArt = '/games/dispatch-dash/dispatch-dash-halloween.webp';
const clearTheLaneHalloweenArt = '/games/dispatch-dash/clear-the-lane-halloween.webp';
const hookAndHaulHalloweenArt = '/games/dispatch-dash/hook-and-haul-halloween.webp';

function gameArtTheme(seasonal) {
  return seasonal.holiday === 'halloween' ? 'halloween' : 'default';
}

function seasonalEdition(theme, fallback) {
  return theme === 'halloween' ? 'HALLOWEEN EDITION' : fallback;
}

export default function Games() {
  const [playing, setPlaying] = useState(null);
  const seasonal = seasonalStateForDate();
  const artTheme = gameArtTheme(seasonal);
  const halloween = artTheme === 'halloween';
  const dispatchArt = halloween ? dispatchDashHalloweenArt : dispatchDashKeyArt;
  const laneArt = halloween ? clearTheLaneHalloweenArt : clearTheLaneKeyArt;
  const hookArt = halloween ? hookAndHaulHalloweenArt : hookAndHaulKeyArt;

  return (
    <>
      <Seo
        title="Games While You Wait"
        description="Play Dispatch Dash, Clear the Lane and J20: Hook & Haul—free towing games designed in-house by Jonny’s Towing."
        canonicalPath="/games"
        image={dispatchArt}
      />
      <section className={`jonnys-games jonnys-games--${artTheme}`}>
        <div className="jonnys-games__glow" aria-hidden="true" />
        <div className="container">
          <header className="jonnys-games__intro">
            <span className="jonnys-games__eyebrow"><Gamepad2 size={17} /> JONNY’S ARCADE</span>
            <h1>A little fun.<br /><span>While you wait.</span></h1>
            <p>A little fun while we’re on our way. Play games designed in-house by Jonny’s Towing.</p>
          </header>

          <GameAccountPanel />
          <div className="jonnys-games__list">
            <article className="jonnys-game-card">
              <div className={`jonnys-game-card__art jonnys-game-card__art--season-${artTheme} jonnys-game-card__art--dispatch`}>
                <img src={dispatchArt} alt="J1, Jonny’s black-and-pink wrecker, towing a secured silver car on a city street" width="1672" height="941" fetchpriority="high" />
                <div className="jonnys-game-card__art-shade" aria-hidden="true" />
                <span className="jonnys-game-card__edition"><Sparkles size={14} /> {seasonalEdition(artTheme, 'MADE BY JONNY’S')}</span>
                <div className="jonnys-game-card__radio" aria-hidden="true"><Radio size={18} /><span><strong>Dispatch to J1…</strong>We’ve got another one for you.</span></div>
              </div>
              <div className="jonnys-game-card__copy">
                <div className="jonnys-game-card__category"><span /> TOWING &amp; ROADSIDE ADVENTURE</div>
                <h2>Dispatch <span>Dash</span></h2>
                <p>Take the wheel of Jonny’s black-and-pink tow truck. Answer dispatch, rescue stranded drivers, and grow your fleet.</p>
                <div className="jonnys-game-card__tags" aria-label="Dispatch Dash features"><span>Explore the city</span><span>Choose the right service</span><span>Build your fleet</span></div>
                <button className="jonnys-game-play" type="button" onClick={() => setPlaying('dispatch-dash')}><Play size={19} fill="currentColor" /> Play Dispatch Dash <ArrowRight size={19} /></button>
                <div className="jonnys-game-card__fine"><Smartphone size={15} /><span>Free to play. No app to download.</span></div>
              </div>
            </article>

            <article className="jonnys-game-card jonnys-game-card--lane">
              <div className={`jonnys-game-card__art jonnys-game-card__art--lane jonnys-game-card__art--season-${artTheme}`}>
                <img src={laneArt} alt="J1, Jonny’s black-and-pink wrecker, waiting for traffic to clear a roadside lane" width="1672" height="941" loading="lazy" />
                <div className="jonnys-game-card__art-shade" aria-hidden="true" />
                <span className="jonnys-game-card__edition"><Sparkles size={14} /> {seasonalEdition(artTheme, 'NEW GAME')}</span>
                <div className="jonnys-game-card__radio" aria-hidden="true"><Truck size={18} /><span><strong>J1 needs a route.</strong>Move traffic and clear the exit.</span></div>
              </div>
              <div className="jonnys-game-card__copy">
                <div className="jonnys-game-card__category"><span /> ROADSIDE TRAFFIC PUZZLE</div>
                <h2>Clear <span>the Lane</span></h2>
                <p>Slide realistic cars, vans and trucks through day, night, rain and protected roadside scenes to make room for J1.</p>
                <div className="jonnys-game-card__tags" aria-label="Clear the Lane features"><span>20 levels</span><span>Realistic roadside scenes</span><span>Saves your stars</span></div>
                <button className="jonnys-game-play" type="button" onClick={() => setPlaying('clear-the-lane')}><Play size={19} fill="currentColor" /> Play Clear the Lane <ArrowRight size={19} /></button>
                <div className="jonnys-game-card__fine"><Smartphone size={15} /><span>Touch, mouse and keyboard friendly.</span></div>
              </div>
            </article>

            <article className="jonnys-game-card jonnys-game-card--hook">
              <div className={`jonnys-game-card__art jonnys-game-card__art--hook jonnys-game-card__art--season-${artTheme}`}>
                <img src={hookArt} alt="J20, Jonny’s black-and-pink flatbed, winching a stuck crossover from a roadside shoulder" width="1672" height="1672" loading="lazy" />
                <div className="jonnys-game-card__art-shade" aria-hidden="true" />
                <span className="jonnys-game-card__edition"><Sparkles size={14} /> {seasonalEdition(artTheme, 'NEW GAME')}</span>
                <div className="jonnys-game-card__radio" aria-hidden="true"><Truck size={18} /><span><strong>J20 is on scene.</strong>Choose your angle and work the line in.</span></div>
              </div>
              <div className="jonnys-game-card__copy">
                <div className="jonnys-game-card__category"><span /> WINCH-OUT &amp; RECOVERY SIM</div>
                <h2>Hook <span>&amp; Haul</span></h2>
                <p>Set up J20’s flatbed for a recovery, pick the right cable angle and hold the pull. Terrain, slopes, weight and winch heat decide how the job goes.</p>
                <div className="jonnys-game-card__tags" aria-label="Hook and Haul features"><span>Real pull physics</span><span>8 recoveries</span><span>J2 assists on heavy jobs</span></div>
                <button className="jonnys-game-play" type="button" onClick={() => setPlaying('hook-and-haul')}><Play size={19} fill="currentColor" /> Play Hook &amp; Haul <ArrowRight size={19} /></button>
                <div className="jonnys-game-card__fine"><Smartphone size={15} /><span>Press and hold PULL—works with touch, mouse or keyboard.</span></div>
              </div>
            </article>
          </div>

          <section className="jonnys-games__how" aria-label="How to enjoy Jonny’s Arcade">
            <div><span><Radio size={21} /></span><p><strong>Pick your game.</strong>Drive dispatch calls, solve a lane puzzle or work a recovery line.</p></div>
            <div><span><Truck size={21} /></span><p><strong>Play while parked.</strong>Enjoy a quick round somewhere safely away from traffic.</p></div>
            <div><span><Wrench size={21} /></span><p><strong>Keep your update handy.</strong>Your real service status stays on the link we sent you.</p></div>
          </section>
          <p className="jonnys-games__note">Waiting for us? Your real service updates stay on the link we sent you.</p>
        </div>
      </section>
      {playing && <DispatchGamePanel game={playing} source="games_page" onClose={() => setPlaying(null)} />}
    </>
  );
}
